import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./PermissionPage.module.css";
import { reactIcons } from "../../icons/reactIcons";

const isIOS = () => {
  return (
    typeof window !== "undefined" &&
    /iPhone|iPad|iPod/i.test(window.navigator.userAgent)
  );
};

const PermissionPage: React.FC = () => {
  const navigate = useNavigate();
  const [locationGranted, setLocationGranted] = useState(false);
  const [cameraGranted, setCameraGranted] = useState(false);

  // --- Removed hidden iOS video hack (causing permission reset) ---
  const iosVideoRef = useRef<HTMLVideoElement | null>(null);

  // --------- CHECK CAMERA PERMISSION ---------
  const checkCameraPermission = async () => {
    try {
      // iOS does NOT support permissions.query for camera => incorrect values
      // Instead, try a non-visible getUserMedia call
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraGranted(true);
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setCameraGranted(false);
    }
  };

  // --------- LOCATION REQUEST (unchanged) ---------
  const requestLocation = async () => {
    try {
      await new Promise<void>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(),
          () => reject()
        );
      });

      setLocationGranted(true);
    } catch {
      alert("Please enable location permission manually.");
    }
  };

  // --------- CAMERA REQUEST (updated: removed iOS hidden video hack) ---------
  const requestCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      setCameraGranted(true);
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      alert("Please enable camera permission manually.");
    }
  };

  // -------- Only check camera on load (location must be manual) --------
  useEffect(() => {
    checkCameraPermission();
  }, []);

  // -------- Redirect when both granted --------
  useEffect(() => {
    if (locationGranted && cameraGranted) {
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 400);
    }
  }, [locationGranted, cameraGranted]);

  return (
    <div className={styles.permissionContainer}>
      <div className={styles.permissionCard}>
        <div className={styles.iconRow}>
          <reactIcons.MapPinIcon className={styles.permissionIcon} />
          <reactIcons.IoCameraIcon className={styles.permissionIcon} />
        </div>

        <h2 className={styles.permissionTitle}>Permissions Required</h2>

        <p className={styles.permissionText}>
          To ensure accurate and verified reporting, we require access to your{" "}
          <strong>Location</strong> and <strong>Camera</strong>.
        </p>

        <div className={styles.reasonBox}>
          <h4>Why we need this:</h4>
          <ul>
            <li>
              📍 <strong>Location</strong> helps us detect where the report is
              being submitted from, ensuring on-site verification.
            </li>
            <li>
              📸 <strong>Camera</strong> allows the inspector to capture and
              upload investigation photos or evidence directly.
            </li>
          </ul>
        </div>

        <div className={styles.statusContainer}>
          <div className={styles.statusRow}>
            <p>📍 Location:</p>
            {locationGranted ? (
              <span className={styles.granted}>Granted</span>
            ) : (
              <button
                className={styles.permissionButton}
                onClick={requestLocation}
              >
                Grant
              </button>
            )}
          </div>

          <div className={styles.statusRow}>
            <p>🎥 Camera:</p>
            {cameraGranted ? (
              <span className={styles.granted}>Granted</span>
            ) : (
              <button
                className={styles.permissionButton}
                onClick={requestCamera}
              >
                Grant
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionPage;
